import React, { type ReactElement, useMemo } from 'react';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import TableActions from 'atom-components/table-actions-lite';
import ServerControlledTable from 'atom-components/tables/server-controlled-table';
import { type SortingProps } from 'atom-components/tables/types';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import { reviewMarkStatuses } from 'helpers/domain-specific/mark-status.helpers';
import { friendlyModes } from 'helpers/domain-specific/response-mode.helpers';
import AllocatedMarkerBadge from 'pages/marking/components/allocated-marker-badge';
import DeallocateMarkerButton from 'pages/marking/components/deallocate-marker-button';
import MarkResponseButton from 'pages/marking/components/mark-response-button';
import CommentLink from 'pages/marking/subpages/marker-dashboard-tab/comment-link';
import type { ConstructedMarker } from 'pages/marking/types';
import {
  MarkStatus,
  MarkType
} from 'services/assist-api/entities/test-instance-response-mark/types';
import type { BaseMarkingTask } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/types';
import type { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';
import DeleteMarkButton from './delete-mark-button';

type Props = {
  markingTasks: BaseMarkingTask[];
  isLoading: boolean;
} & SortingProps;

export default function MarkerDashboardTable({
  markingTasks,
  isLoading,
  initialSorting,
  onSort
}: Props): ReactElement {
  type MarkingTaskRow = {
    nsn: string;
    responseId: number;
    markId?: number;
    test: string;
    mode: TestInstanceResponseInputType[];
    markStatus: MarkStatus | null;
    marker: ConstructedMarker;
    review: boolean;
    comment: boolean;
  };

  const columnHelper = createColumnHelper<MarkingTaskRow>();
  const columns: ColumnDef<MarkingTaskRow>[] = useMemo(
    () => [
      columnHelper.accessor('nsn', {
        header: () => 'NSN',
        size: 100
      }),
      columnHelper.accessor('responseId', {
        header: () => 'Response ID',
        cell: ({ row }) => row.original.responseId,
        size: 100
      }),
      columnHelper.accessor('markId', {
        header: () => 'Mark ID',
        cell: ({ row }) => row.original.markId ?? 'Unstarted',
        size: 80
      }),
      columnHelper.accessor('test', {
        header: () => 'Test',
        cell: ({ row }) => <span>{row.original.test}</span>,
        minSize: 180,
        size: 180,
        maxSize: 180
      }),
      columnHelper.accessor('mode', {
        header: () => 'Mode',
        cell: ({ row }) => friendlyModes(row.original.mode),
        size: 60
      }),
      columnHelper.accessor('markStatus', {
        header: () => 'Mark Status',
        cell: ({ row }) => (
          <MarkStatusBadge type={MarkType.Manual}>
            {row.original.markStatus ?? MarkStatus.Assigned}
          </MarkStatusBadge>
        )
      }),
      columnHelper.accessor('marker.id', {
        id: 'marker',
        header: () => 'Marker',
        cell: ({ row }) => <AllocatedMarkerBadge marker={row.original.marker} />
      }),
      columnHelper.accessor('review', {
        header: () => 'Review',
        enableSorting: false,
        cell: ({ row }) => <span>{row.original.review ? 'Y' : 'N'}</span>,
        size: 50
      }),
      columnHelper.accessor('comment', {
        header: () => 'Comment',
        cell: ({ row }) => (
          <CommentLink
            hasComment={row.original.comment}
            responseId={row.original.responseId}
            markId={row.original.markId}
          />
        ),
        size: 70
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <TableActions>
            <MarkResponseButton responseId={row.original.responseId} markId={row.original.markId} />
            <DeallocateMarkerButton
              responseId={row.original.responseId}
              allocatedMarkerId={row.original.marker.id}
              allocatedOrganisationUser={row.original.marker.organisationUser}
              markId={row.original.markId}
            />
            <DeleteMarkButton
              markId={row.original.markId}
              markStatus={row.original.markStatus ?? undefined}
            />
          </TableActions>
        ),
        size: 80
      })
    ],
    [columnHelper]
  );

  const data: MarkingTaskRow[] = useMemo(
    () =>
      markingTasks.map((markingTask) => ({
        nsn: markingTask.customerUserIdentifier,
        responseId: markingTask.testInstanceResponseId,
        markId: markingTask.testInstanceResponseMarkId || undefined, // Treat markId as optional.
        test: markingTask.testName,
        mode: markingTask.testInstanceResponseInputTypes,
        markStatus: markingTask.testInstanceResponseMarkStatus,
        marker: {
          id: markingTask.testInstanceResponseMarkerId,
          organisationUser: {
            id: markingTask.testInstanceResponseMarkerCustomersUsersId,
            user: {
              id: markingTask.testInstanceResponseMarkerCustomersUsersUserId,
              firstname: markingTask.testInstanceResponseMarkerCustomersUsersUserFirstname,
              lastname: markingTask.testInstanceResponseMarkerCustomersUsersUserLastname,
              username: markingTask.testInstanceResponseMarkerCustomersUsersUserUsername
            }
          }
        },
        review: reviewMarkStatuses.includes(markingTask.testInstanceResponseMarkStatus),
        comment:
          markingTask.testInstanceResponseMarkComment !== '' &&
          markingTask.testInstanceResponseMarkComment !== null
      })),
    [markingTasks]
  );

  return (
    <ServerControlledTable
      isLoading={isLoading}
      data={data}
      columns={columns}
      initialSorting={initialSorting}
      onSort={onSort}
      entity={['response', 'mark']}
    />
  );
}
